// src/Privacy.js
import React from 'react';
import { Link } from 'react-router-dom';
import './App.css';

function Privacy() {
  return (
    <div className="left-align">
      <h1>Privacy Policy</h1>
      <p>Last updated: 4 October 2026</p>
      <p>
        Fampack is run by Allan Dong Fang Zhang. If you have questions about
        this policy or your information, contact us at{' '}
        <a href="mailto:team@fampack.net">team@fampack.net</a>.
      </p>

      <h2>What Fampack collects and why</h2>
      <p>We collect the information needed to run Fampack:</p>
      <ul>
        <li>your phone number or email address, to sign you in and contact you about your account;</li>
        <li>your user ID, username and profile photo, so people can find and recognize your account;</li>
        <li>posts you create, including photos, videos, titles and captions;</li>
        <li>comments, likes, saved posts and seen posts, to show feeds and keep your place in the app;</li>
        <li>follows, friendships, friend requests and blocks, to connect people and enforce privacy choices;</li>
        <li>reports and moderation records, so the team can review safety issues;</li>
        <li>creator application details and Android waitlist reservations, to review applications and manage launch access.</li>
      </ul>
      <p>
        Fampack does not sell personal data, track people across other companies'
        apps or websites, or show third-party ads.
      </p>

      <h2>Usage analytics</h2>
      <p>
        When you are signed in to the iPhone app, Fampack collects usage
        analytics linked to your account so we can run and improve the service.
        This includes app sessions and foreground time; screens, feeds, posts,
        videos, likes, comments opened, searches, profiles, follows, composer
        progress, communities and error messages shown. Search analytics include
        the search query you typed. Video and diagnostic analytics include
        start-up time, stalls, playback failures, app version, iOS version and
        device model.
      </p>
      <p>
        Usage analytics do not include message, caption, title or comment text,
        phone numbers, email addresses, contacts, location or photo-library
        contents. They are not used for advertising or tracking, are not sold,
        and are not shared except with service providers that process them for
        Fampack.
      </p>
      <p>
        The team can review an account's activity when operating Fampack,
        investigating issues and understanding product use. Raw usage events are
        kept for 90 days unless the storage cap requires older events to be
        deleted sooner. Daily summaries linked to your account are kept until
        your account is deleted. Daily totals that do not identify an account may
        be kept indefinitely. Deleting your account removes your raw events and
        account-linked daily summaries; past aggregate totals stay.
      </p>
      <p>
        Usage analytics cannot be turned off in the app. The app does not ask
        for tracking permission for this collection because Fampack does not
        track you across other companies' apps or websites.
      </p>

      <h2>Service providers</h2>
      <p>We use service providers to operate Fampack:</p>
      <ul>
        <li>Supabase provides the database, account authentication and sign-in codes.</li>
        <li>Twilio sends text messages, including sign-in and deletion-related messages.</li>
        <li>Cloudflare stores and delivers photos and videos.</li>
        <li>Resend sends email, including sign-in and deletion-related messages.</li>
      </ul>
      <p>
        These providers process information for Fampack so they can provide their
        services to us.
      </p>

      <h2>Moderation and safety</h2>
      <p>
        Fampack has a zero-tolerance policy for objectionable content and abusive
        users. The service automatically masks listed words in new post titles,
        captions and comments. People can report content or accounts, and can
        block accounts. Blocks hide each account from the other and stop follows,
        friendships and friend requests between them.
      </p>
      <p>
        Moderators review reports and may remove content, suspend accounts or ban
        accounts. Removed content is hidden from the app, but may stay in the
        admin tools until an admin deletes it for good.
      </p>

      <h2>Deleting your account</h2>
      <p>
        In the iPhone app, go to Settings → Account → Delete account. Suspended
        or banned accounts can also start deletion from the locked screen. The
        website cannot delete or cancel an account deletion.
      </p>
      <p>
        Deletion requires a fresh sign-in code. Once it is scheduled, every
        device is signed out, the account is hidden immediately, and the account
        is deleted 30 days later. Signing in to the iPhone app before deletion
        starts cancels the deletion.
      </p>
      <p>
        The purge deletes the profile, username, phone number, email address,
        profile photo, posts and their media, comments and replies under them,
        likes, saved posts, seen posts, follows, friendships, friend requests,
        blocks, creator applications, Android waitlist reservations and sign-in
        identity. For creators, the creator's community and fan posts in it are
        deleted too. The username becomes available again after the purge.
      </p>
      <p>
        Photos and videos are deleted from storage. Copies that the content
        network already cached may keep loading from their old addresses until
        the network evicts them. If you ask us, we will purge those cached media
        prefixes by hand.
      </p>
      <p>After an account is purged, Fampack keeps only what is needed for safety:</p>
      <ul>
        <li>reports the account filed, with the reporter removed;</li>
        <li>content a moderator removed, until an admin deletes it for good, no longer linked to the deleted account;</li>
        <li>for a banned account, a keyed one-way hash of its phone number and email address, with the ban reason, to stop ban evasion.</li>
      </ul>

      <h2>Children</h2>
      <p>
        Fampack is not directed at children under 13. We do not knowingly collect
        personal information from children under 13. A parent or guardian can ask
        us to delete a child's account by emailing{' '}
        <a href="mailto:team@fampack.net">team@fampack.net</a>.
      </p>

      <h2>Your choices and rights</h2>
      <p>
        You can ask to access, correct or delete personal data by emailing{' '}
        <a href="mailto:team@fampack.net">team@fampack.net</a>. You can also
        delete your account in the app as described above.
      </p>
      <p>
        To report content or accounts, or to learn more about account deletion,
        visit <Link to="/support">Support</Link>.
      </p>

      <h2>Security and changes</h2>
      <p>
        We use reasonable safeguards to protect the information Fampack processes
        and maintains. We may update this policy when Fampack changes. The latest
        version is posted on this page.
      </p>
    </div>
  );
}

export default Privacy;
